import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { X, Package, DollarSign, Image as ImageIcon, Info, Layers, AlertCircle, HelpCircle } from 'lucide-react';
import ImageUploader from './ImageUploader';
import PriceTierManager from './PriceTierManager';
import { useNotification } from '../../context/NotificationContext';


// GraphQL Mutation
const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
      name
      description
      category
      subcategory
      images  {
        thumbnail
        small
        medium
        large
        full
        original
      }
      basePrice
      currentPrice
      priceTiers {
        groupSize
        price
      }
      unit
      quantity
      brand
      manufacturer
      sku
      dimensions {
        length
        width
        height
        unit
      }
      weight {
        value
        unit
      }
      variations {
        name
        options
      }
      available
      approvalStatus
      createdAt
    }
  }
`;

// Product categories
const CATEGORIES = [
  'Produce',
  'Bakery',
  'Dairy',
  'Meat & Seafood',
  'Frozen Foods',
  'Pantry Staples',
  'Beverages',
  'Snacks',
  'Health & Wellness',
  'Household',
  'Electronics',
  'Clothing',
  'Home & Garden',
  'Books & Media',
  'Toys & Games',
  'Sports & Outdoors',
  'Other'
];

const UNITS = [
  'each', 'lb', 'oz', 'kg', 'g', 'dozen', 'box', 'bag', 'bundle', 'case', 'pack', 'gallon', 'liter', 'pint', 'quart'
];

const AddProductModal = ({ isOpen, onClose, onProductAdded }) => {

  console.log('AddProductModal rendering');
  console.log('Cloudinary env vars:', {
    name: process.env.REACT_APP_CLOUDINARY_NAME,
    preset: process.env.REACT_APP_CLOUDINARY_PRESET,
    allEnvKeys: Object.keys(process.env || {})
  });



  const { addNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('basic');
  
  // Form state - Added retailPrice for B19 savings calculation
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    basePrice: '',
    retailPrice: '', // Competitor retail price for savings calculation (B19)
    unit: 'each',
    quantity: '',
    brand: '',
    manufacturer: '',
    sku: '',
    available: true,
    // Dimensions
    length: '',
    width: '',
    height: '',
    dimensionUnit: 'in',
    // Weight
    weight: '',
    weightUnit: 'lb'
  });
  
  const [images, setImages] = useState([]);
  const [priceTiers, setPriceTiers] = useState([]);
  const [variations, setVariations] = useState([]);
  const [bulletPoints, setBulletPoints] = useState(['', '', '']);
  const [errors, setErrors] = useState({});
  const [showRetailTooltip, setShowRetailTooltip] = useState(false);
  
  

  const [createProduct, { loading }] = useMutation(CREATE_PRODUCT, {
    onCompleted: (data) => {
      addNotification({
        type: 'success',
        title: 'Product Created!',
        message: 'Your product has been submitted for approval.',
        showToast: true
      });
      
      if (onProductAdded) {
        onProductAdded(data.createProduct);
      }
      
      handleClose();
    },
    onError: (error) => {
      console.error('Create product error:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Create Product',
        message: error.message || 'Please try again.',
        showToast: true
      });
    }
  });

  const handleClose = () => {
    // Reset form
    setFormData({
      name: '',
      description: '',
      category: '',
      subcategory: '',
      basePrice: '',
      retailPrice: '',
      unit: 'each',
      quantity: '',
      brand: '',
      manufacturer: '',
      sku: '',
      available: true,
      length: '',
      width: '',
      height: '',
      dimensionUnit: 'in',
      weight: '',
      weightUnit: 'lb'
    });
    setImages([]);
    setPriceTiers([]);
    setVariations([]);
    setBulletPoints(['', '', '']);
    setErrors({});
    setActiveTab('basic');
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBulletPointChange = (index, value) => {
    const newBulletPoints = [...bulletPoints];
    newBulletPoints[index] = value;
    setBulletPoints(newBulletPoints);
  };

  const addBulletPoint = () => {
    setBulletPoints([...bulletPoints, '']);
  };

  const removeBulletPoint = (index) => {
    setBulletPoints(bulletPoints.filter((_, i) => i !== index));
  };

  const addVariation = () => {
    setVariations([...variations, { name: '', options: [''] }]);
  };

  const updateVariation = (index, field, value) => {
    const newVariations = [...variations];
    newVariations[index][field] = value;
    setVariations(newVariations);
  };

  const updateVariationOption = (varIndex, optIndex, value) => {
    const newVariations = [...variations];
    newVariations[varIndex].options[optIndex] = value;
    setVariations(newVariations);
  };

  const addVariationOption = (varIndex) => {
    const newVariations = [...variations];
    newVariations[varIndex].options.push('');
    setVariations(newVariations);
  };

  const removeVariation = (index) => {
    setVariations(variations.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
      newErrors.basePrice = 'Valid base price is required';
    }
    
    // Image validation
    if (images.length === 0) {
      newErrors.images = 'At least one image is required';
    }
    
    // Retail price validation (optional but should be > base price if provided)
    if (formData.retailPrice && parseFloat(formData.retailPrice) <= parseFloat(formData.basePrice)) {
      newErrors.retailPrice = 'Retail price should be higher than your UniLedge price';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  console.log('ENV Check:', {
    cloudName: process.env.REACT_APP_CLOUDINARY_NAME,
    preset: process.env.REACT_APP_CLOUDINARY_PRESET,
    allEnv: Object.keys(process.env).filter(key => key.startsWith('REACT_APP'))
  });




  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (loading) return;
    
    if (!validateForm()) {
      return;
    }
    
    try {

      // STEP 1: Upload images directly to Cloudinary FIRST
      const uploadedImageUrls = [];
        
      for (const image of images) {
        const uploadFormData = new FormData();

        // Check what format the image is in
        if (image.file) {
          // If it's from the simplified ImageUploader (has a file property)
          uploadFormData.append('file', image.file);
        } else if (image.data || image.preview) {
          // If it's base64 (from old uploader or existing code)
          const base64Data = image.data || image.preview;
          const response = await fetch(base64Data);
          const blob = await response.blob();
          uploadFormData.append('file', blob);
        } 
          // If it's directly a File object
        

        uploadFormData.append('upload_preset', 'uniledge_products');

        // Upload directly to Cloudinary
        const cloudinaryResponse = await fetch(
          `https://api.cloudinary.com/v1_1/drcldizc0/image/upload`,
          {
            method: 'POST',
            body: uploadFormData
          }
        );

        

        const cloudinaryData = await cloudinaryResponse.json();

        // For now, just push the URL string (backend expects array of strings based on your schema)
        uploadedImageUrls.push({
          thumbnail: cloudinaryData.secure_url,
          small: cloudinaryData.secure_url,
          medium: cloudinaryData.secure_url,
          large: cloudinaryData.secure_url,
          full: cloudinaryData.secure_url,
          original: cloudinaryData.secure_url
        });
      }

      // STEP 2: Prepare input with Cloudinary URLs
        // Store only the URL (not base64!)
        
      // Prepare input - includes retailPrice for B19 savings calculation
      const input = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        subcategory: formData.subcategory || null,
        basePrice: parseFloat(formData.basePrice),
        // B19 FIX: Include retail price for savings calculation
        retailPrice: formData.retailPrice ? parseFloat(formData.retailPrice) : null,
        unit: formData.unit,
        quantity: parseInt(formData.quantity) || 0,
        brand: formData.brand.trim() || null,
        manufacturer: formData.manufacturer.trim() || null,
        sku: formData.sku.trim() || null,
        available: formData.available,
        legacyImages: uploadedImageUrls.map(img => img.original || img.thumbnail), // Array of URL strings from Cloudinary
        priceTiers: priceTiers.map(tier => ({
          groupSize: parseInt(tier.groupSize),
          price: parseFloat(tier.price)
        })),
        dimensions: (formData.length || formData.width || formData.height) ? {
          length: parseFloat(formData.length) || null,
          width: parseFloat(formData.width) || null,
          height: parseFloat(formData.height) || null,
          unit: formData.dimensionUnit
        } : null,
        weight: formData.weight ? {
          value: parseFloat(formData.weight),
          unit: formData.weightUnit
        } : null,
        variations: variations
          .filter(v => v.name && v.options.some(o => o))
          .map(v => ({
            name: v.name,
            options: v.options.filter(o => o)
          })),
        attributes: {
          bulletPoints: bulletPoints.filter(bp => bp.trim())
        }
      };

      console.log('Sending to backend - payload size:', JSON.stringify(input).length, 'bytes');
      console.log('Images are now URLs:', uploadedImageUrls);

      // STEP 3: Send to backend (only URLs, tiny payload!)
      await createProduct({ variables: { input } });

      // Success notification
      addNotification({
        type: 'success',
        title: 'Product Created',
        message: 'Your product has been successfully created!',
        showToast: true
      });

      // Close modal or reset form
      onClose();

    } catch (error) {
      console.error('Submit error:', error);

      // Better error handling
      if (error.message.includes('413')) {
        // This should never happen now, but keeping as safety
        addNotification({
          type: 'error',
          title: 'File Size Too Large',
          message: 'One or more images exceed the size limit. Please use images under 5MB each.',
          showToast: true
        });
      } else if (error.message.includes('Cloudinary')|| error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        addNotification({
          type: 'error',
          title: 'Image Upload Failed',
          message: 'Unable to upload images. Please switch to the Images tab to verify your images are valid (JPG/PNG under 5MB). You can remove problematic images and try again.',
          showToast: true
        });
      } else if (error.message.includes('Network') || error.message.includes('network')) {
        addNotification({
          type: 'error',
          title: 'Network Error',
          message: 'Please check your internet connection and try again. If uploading multiple images, try fewer at once.',
          showToast: true
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Failed to Create Product',
          message: error.message || 'Please check all required fields and try again.',
          showToast: true
        });
      }
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Package },
    { id: 'images', label: 'Images', icon: ImageIcon },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'details', label: 'Details', icon: Info },
    { id: 'variations', label: 'Variations', icon: Layers }
  ];

  // Calculate potential savings display for vendor feedback
  const potentialSavings = formData.retailPrice && formData.basePrice 
    ? (parseFloat(formData.retailPrice) - parseFloat(formData.basePrice)).toFixed(2)
    : null;
  
  const savingsPercentage = formData.retailPrice && formData.basePrice
    ? Math.round(((parseFloat(formData.retailPrice) - parseFloat(formData.basePrice)) / parseFloat(formData.retailPrice)) * 100)
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Add New Product</h2>
            <p className="text-sm text-gray-600 mt-1">Fill in the details to list your product</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const hasError = 
                (tab.id === 'basic' && (errors.name || errors.category || errors.basePrice)) ||
                (tab.id === 'images' && errors.images) ||
                (tab.id === 'pricing' && errors.retailPrice);
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon size={18} />
                  <span className="font-medium">{tab.label}</span>
                  {hasError && (
                    <AlertCircle size={16} className="text-red-500" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Premium Organic Apples"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your product in detail..."
                />
              </div>

              {/* Category & Subcategory */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.category ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subcategory
                  </label>
                  <input
                    type="text"
                    name="subcategory"
                    value={formData.subcategory}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Apples, Bread"
                  />
                </div>
              </div>

              {/* Brand & Manufacturer */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brand
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Farm Fresh"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Local Farms Co"
                  />
                </div>
              </div>

              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SKU (Optional - will be auto-generated if left blank)
                </label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., SKU-001"
                />
              </div>

              {/* Bullet Points */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Features (Bullet Points)
                </label>
                {bulletPoints.map((point, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={point}
                      onChange={(e) => handleBulletPointChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Feature ${index + 1}`}
                    />
                    {bulletPoints.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBulletPoint(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addBulletPoint}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Add Feature
                </button>
              </div>

              {/* Availability */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="available"
                  checked={formData.available}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="ml-3 text-sm font-medium text-gray-700">
                  Product is available for sale
                </label>
              </div>
            </div>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Product Images</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add high-quality images of your product. The first image will be the primary display image.
                </p>
                  
                {/* Image Requirements Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Image Guidelines:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Use high-quality photos with good lighting</li>
                    <li>• Show product from multiple angles</li>
                    <li>• Include size reference if applicable</li>
                    <li>• Images are automatically optimized for fast loading</li>
                  </ul>
                </div>
              </div>

              <ImageUploader 
                images={images}
                setImages={setImages}
                maxImages={10}
              />

              {errors.images && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.images}
                </p>
              )}
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              {/* Base Price, Unit, Quantity */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your UniLedge Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      name="basePrice"
                      value={formData.basePrice}
                      onChange={handleChange}
                      className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.basePrice ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.basePrice && (
                    <p className="mt-1 text-sm text-red-600">{errors.basePrice}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit
                  </label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {UNITS.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity Available
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* B19 FIX: Competitor Retail Price Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">💰</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Competitor Retail Price</h4>
                      <p className="text-sm text-gray-600">Help customers see their savings!</p>
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowRetailTooltip(!showRetailTooltip)}
                      className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-white/50"
                    >
                      <HelpCircle size={18} />
                    </button>
                    {showRetailTooltip && (
                      <div className="absolute right-0 top-8 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10">
                        <h5 className="font-medium text-gray-900 mb-2">Why add this?</h5>
                        <ul className="text-sm text-gray-600 space-y-2">
                          <li>• <strong>Show value:</strong> Customers see exactly how much they save vs competitors</li>
                          <li>• <strong>Build trust:</strong> Transparent pricing builds confidence</li>
                          <li>• <strong>Boost sales:</strong> Visible savings encourage purchases</li>
                          <li>• <strong>Analytics:</strong> Track total savings your products provide</li>
                        </ul>
                        <p className="text-xs text-gray-500 mt-3 pt-2 border-t">
                          Enter the typical price at Walmart, Target, Amazon, etc.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Average Retail Price (at competitors)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        name="retailPrice"
                        value={formData.retailPrice}
                        onChange={handleChange}
                        className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                          errors.retailPrice ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="e.g., 12.99"
                      />
                    </div>
                    {errors.retailPrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.retailPrice}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Price at Walmart, Target, Amazon, or similar stores
                    </p>
                  </div>
                  
                  {/* Savings Preview */}
                  {potentialSavings && potentialSavings > 0 && (
                    <div className="bg-white rounded-lg border border-green-300 p-4 text-center min-w-[140px]">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Customer Saves</p>
                      <p className="text-2xl font-bold text-green-600">${potentialSavings}</p>
                      <p className="text-sm text-green-600 font-medium">{savingsPercentage}% off retail</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Tiers */}
              <div className="border-t border-gray-200 pt-6">
                <PriceTierManager
                  basePrice={formData.basePrice}
                  priceTiers={priceTiers}
                  setPriceTiers={setPriceTiers}
                />
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Dimensions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Dimensions (Optional)</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Length</label>
                    <input
                      type="number"
                      step="0.01"
                      name="length"
                      value={formData.length}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Width</label>
                    <input
                      type="number"
                      step="0.01"
                      name="width"
                      value={formData.width}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                    <input
                      type="number"
                      step="0.01"
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                    <select
                      name="dimensionUnit"
                      value={formData.dimensionUnit}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="in">inches</option>
                      <option value="cm">cm</option>
                      <option value="ft">feet</option>
                      <option value="m">meters</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Weight */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Weight (Optional)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
                    <input
                      type="number"
                      step="0.01"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                    <select
                      name="weightUnit"
                      value={formData.weightUnit}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="lb">pounds (lb)</option>
                      <option value="oz">ounces (oz)</option>
                      <option value="kg">kilograms (kg)</option>
                      <option value="g">grams (g)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Variations Tab */}
          {activeTab === 'variations' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Product Variations</h3>
                  <p className="text-sm text-gray-600">Add variations like size, color, flavor, etc.</p>
                </div>
                <button
                  type="button"
                  onClick={addVariation}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  + Add Variation
                </button>
              </div>

              {variations.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <Layers className="mx-auto mb-2 text-gray-400" size={32} />
                  <p className="text-sm text-gray-600">No variations added yet</p>
                  <p className="text-xs text-gray-500 mt-1">Variations are optional</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {variations.map((variation, varIndex) => (
                    <div key={varIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <input
                          type="text"
                          value={variation.name}
                          onChange={(e) => updateVariation(varIndex, 'name', e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Variation name (e.g., Size, Color)"
                        />
                        <button
                          type="button"
                          onClick={() => removeVariation(varIndex)}
                          className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      
                      <div className="space-y-2 ml-4">
                        {variation.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => updateVariationOption(varIndex, optIndex, e.target.value)}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder={`Option ${optIndex + 1}`}
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addVariationOption(varIndex)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          + Add Option
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>       

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              * Required fields • Product will be pending approval
            </p>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Create Product'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;